import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { message } from 'components';

import {
  getDashboardPermissions,
  PermissionsType,
  getExternalAppointment,
  getDashOfferDetails,
  getRetsOfferDetails,
  OfferDetailsType,
  getLockboxPhotoByDashboard,
} from 'api/AppExternalController';
import { getExternalFeedback } from 'api/FeedbackController';
import { getExternalAppointmentSignature } from 'api/Covid19Controller';

import errorReporter from '@app/errorReporter';

import { useSearchParams } from 'hooks';
import { ExternalAuthContext, CoopDashUser } from 'contexts';
import { AppointmentActionModal } from 'modals';
import { featureEnabled, openCovidSignatureAlert } from 'utils';

import { ExternalFeedback } from 'api/FeedbackController/getExternalFeedback';
import ShowingDetailsPage from './ShowingDetailsPage';
import {
  ShowingDetailsAppointmentType,
  ShowingDetailsListingType,
} from './types';

import Loading from '../MyBusinessPage/components/Loading';

export type ExternalShowingDetailsPageProps = {};

const ExternalShowingDetailsPage: React.FC<
  ExternalShowingDetailsPageProps
> = () => {
  const { appointmentId, dashboardId } = useParams<{
    appointmentId: string;
    dashboardId: string;
  }>();
  const queryParams = useSearchParams();
  const history = useHistory();
  const [appointment, setAppointment] =
    useState<ShowingDetailsAppointmentType>();
  const [listing, setListing] = useState<ShowingDetailsListingType>();
  const [feedbackConfig, setFeedbackConfig] = useState<ExternalFeedback>();

  const [loading, setLoading] = useState(false);

  // If appointmentId is not valid, redirect to the showings list
  useEffect(() => {
    if (!appointmentId) {
      errorReporter.error(
        `Invalid appointmentId in URL: ${appointmentId}`,
        new Error('Invalid appointmentId format'),
      );
      history.replace(`/dash/${dashboardId}/appointments`);
    }
  }, [appointmentId, dashboardId, history]);
  const [fetchingOffer, setFetchingOffer] = useState(false);

  const [fetchingPermissions, setFetchingPermissions] = useState(false);

  const [lockboxPhoto, setLockBoxPhoto] = useState<string>('');

  const { auth } = useContext(ExternalAuthContext);
  const userType =
    auth?.user && (auth.user as any).contactType === 'coop' ? 'coop' : 'client';

  const isCustomContact =
    auth?.user &&
    'contactType' in auth.user &&
    (auth.user.contactType === 'customagent' ||
      auth.user.contactType === 'nonagent');

  const [offerDetails, setOfferDetails] = useState<OfferDetailsType>({
    offerCount: 0,
    offerSettings: undefined,
    offerSubmissions: '',
  });

  const [appointmentPermissions, setAppointmentPermissions] =
    useState<PermissionsType>({
      new: false,
      confirm: false,
      deny: false,
      cancel: false,
      attempted: false,
      time_change: false,
    });

  const [signatureType, setSignatureType] = useState<'template' | 'pdf'>(
    'template',
  );

  const fetchAppointmentDetails = async (
    appointmentInput: ShowingDetailsAppointmentType,
  ) => {
    try {
      const [fetchedAppointmentSignature, lockboxPhotoRes] = await Promise.all([
        !appointmentInput.covid19Overridden && appointmentInput.covid19Signed
          ? getExternalAppointmentSignature({
              appointmentId: appointmentInput.id,
              dashboardInfo: {
                dashboardId: auth?.dashboardId || '',
                retsId: auth?.retsId || '',
              },
            })
          : undefined,
        [
          'SentriLockV2',
          'MasterLock',
          'Supra eKEY',
          'Supra Lockbox',
          'Lock Box',
        ].includes(appointmentInput.appointmentConfig?.access) &&
        userType === 'coop'
          ? getLockboxPhotoByDashboard(dashboardId)
          : undefined,
      ]);
      if (fetchedAppointmentSignature) {
        setSignatureType(
          fetchedAppointmentSignature.template ? 'template' : 'pdf',
        );
      }

      if (lockboxPhotoRes) {
        const {
          data: { lockboxPhoto: photoURL },
        } = lockboxPhotoRes;

        if (photoURL) {
          setLockBoxPhoto(photoURL);
        }
      }
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const fetchAppointment = async (needFeedback?: boolean) => {
    if (loading || !auth?.listing) return;

    setLoading(true);
    let fetchedAppointment: ShowingDetailsAppointmentType;
    try {
      [fetchedAppointment] = await Promise.all([
        getExternalAppointment(dashboardId, appointmentId),
        needFeedback && fetchFeedback(),
      ]);

      setAppointment(fetchedAppointment);
      setListing(auth!.listing);
    } catch (err) {
      const msg = 'Error fetching appointment info';
      errorReporter.error(`${msg}: ${err}`);
      message.error(msg);
      return;
    }
    setLoading(false);

    // Appointment Details after
    if (fetchedAppointment) await fetchAppointmentDetails(fetchedAppointment);
  };

  useEffect(() => {
    if (!appointment) fetchAppointment(true);
  }, [auth, appointment]);

  useEffect(() => {
    if (userType === 'coop') {
      const fetchOfferDetails = async () => {
        try {
          setFetchingOffer(true);
          let fetchedOfferDetails: OfferDetailsType = {
            offerCount: 0,
            offerSettings: undefined,
            offerSubmissions: '',
          };

          if (auth?.retsId) {
            fetchedOfferDetails = await getRetsOfferDetails(auth.retsId);
          } else {
            fetchedOfferDetails = await getDashOfferDetails(dashboardId);
          }
          setOfferDetails(fetchedOfferDetails);
        } finally {
          setFetchingOffer(false);
        }
      };

      if (auth?.hasFeature('OFFERS')) {
        fetchOfferDetails();
      }
    }
  }, [userType, auth?.retsId, auth?.hasFeature, dashboardId]);

  useEffect(() => {
    if (userType === 'client' && appointment?.status) {
      const fetchPermissions = async () => {
        setFetchingPermissions(true);
        const fetchedPermissions = await getDashboardPermissions(
          dashboardId,
          appointmentId,
        );
        setAppointmentPermissions(fetchedPermissions);
        setFetchingPermissions(false);
      };

      fetchPermissions();
    }
  }, [userType, dashboardId, appointmentId, appointment?.status]);

  useEffect(() => {
    if (queryParams.sign === 'true' && appointment?.covid19Signed) {
      openCovidSignatureAlert(!!appointment?.covid19Overridden);
    }
  }, [
    queryParams.sign,
    appointment?.covid19Signed,
    appointment?.covid19Overridden,
  ]);

  const fetchFeedback = async () => {
    const config = await getExternalFeedback({
      dashboardId,
      appointmentId,
    });
    setFeedbackConfig(config);
  };

  if (!auth) return null;

  if (!featureEnabled('showingDetailsRevampFeaturePhase', 2)) {
    history.replace({
      ...history.location,
      pathname: `/dash/${dashboardId}/appointments/${appointmentId}`,
    });
  }

  if (!appointment || loading || fetchingOffer || fetchingPermissions) {
    return <Loading />;
  }

  return (
    <StyledExternalShowingDetailsPageContainer>
      {listing && (
        <ShowingDetailsPage
          auth={{
            id: (auth.user as CoopDashUser).id,
            org: {
              country:
                auth.ownerOrg?.country || (auth.user as CoopDashUser).country,
            },
            hasFeature: auth.hasFeature,
          }}
          userType={userType}
          appointment={appointment}
          fetchAppointment={fetchAppointment}
          listing={listing}
          feedbackConfig={feedbackConfig}
          setFeedbackConfig={setFeedbackConfig}
          fetchFeedback={fetchFeedback}
          lockboxPhoto={lockboxPhoto}
          offerDetails={offerDetails}
          signatureType={signatureType}
          dashboardInfo={{
            dashboardId: auth?.dashboardId || '',
            retsId: auth?.retsId || '',
          }}
          appointmentPermissions={appointmentPermissions}
          queryParams={queryParams}
          isCustomContact={isCustomContact}
          isExternal
        />
      )}
      <AppointmentActionModal dashboardId={dashboardId} />
    </StyledExternalShowingDetailsPageContainer>
  );
};

export default ExternalShowingDetailsPage;

const StyledExternalShowingDetailsPageContainer = styled.div`
  margin-bottom: 20px;
`;
